/* ASCEND lesson engine — renders a structured lesson and its interactions.
   Dependency-free; reads lesson data from window.ASCEND_LESSONS[id]. */
(() => {
  "use strict";

  const id = new URLSearchParams(location.search).get("id") || "security";

  /* fundamentals order (by id). */
  const ORDER = [
    "llm-basics",
    "agent-loop",
    "first-task",
    "capabilities",
    "security",
    "prompting",
    "verification",
    "git",
    "instructions",
    "skills",
    "cloud-local",
    "limits-models",
  ];
  const data = (window.ASCEND_LESSONS || {})[id];
  const root = document.getElementById("lesson");
  if (!data) {
    root.innerHTML = '<p class="miss mono">Lesson not found.</p>';
    return;
  }
  document.title = data.title + " · ASCEND";

  /* ---- progress persistence (localStorage) ---- */
  const KEY = "ascend:progress:" + id;
  const store = JSON.parse(localStorage.getItem(KEY) || "{}");
  const save = () => localStorage.setItem(KEY, JSON.stringify(store));

  const total = data.blocks.filter((b) => b.type === "quiz" || b.type === "approve").length;
  const doneCount = () => Object.keys(store).filter((k) => /^cp\d+$/.test(k)).length;

  /* ---- helpers ---- */
  const el = (tag, cls, html) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };
  const esc = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  /* ---- header ---- */
  const head = el("header", "lesson-head reveal");
  head.innerHTML = `
    <a class="back mono" href="/pages/learn">&lsaquo; fundamentals</a>
    <span class="eyebrow mono">// ${data.tag}</span>
    <h1>${data.title}</h1>
    <p class="lesson-sum">${data.summary}</p>
    <p class="lesson-meta mono">${data.estMinutes} min read &middot; <span id="cp-count"></span></p>`;
  root.appendChild(head);

  /* ---- blocks ---- */
  data.blocks.forEach((b, i) => root.appendChild(renderBlock(b, i)));
  root.appendChild(ctaBlock());
  updateCount();
  initMotion();
  buildPager();

  /* ---- end-of-lesson CTA into the community (the one intentional button) ---- */
  function ctaBlock() {
    return el("div", "lesson-cta reveal", `
      <p class="cta-lead">Got a question this lesson didn't answer?</p>
      <a class="cta-btn" href="/pages/community">
        <span class="cta-label">Join The Ascent</span>
        <span class="cta-arrow" aria-hidden="true">&rarr;</span>
      </a>
      <p class="cta-sub mono">ask anything &middot; share what you're building</p>`);
  }

  function updateCount() {
    const n = doneCount();
    const label = n >= total && total > 0 ? `${total}/${total} checkpoints &middot; complete` : `${n}/${total} checkpoints`;
    const t = document.getElementById("cp-count");
    if (t) t.innerHTML = label;
  }

  function renderBlock(b, i) {
    switch (b.type) {
      case "prose":    return el("div", "block prose reveal", b.html);
      case "heading":  return el("h2", "block lesson-h reveal", b.text);
      case "tools":    return toolsBlock(b);
      case "quiz":     return quizBlock(b, i);
      case "approve":  return approveBlock(b, i);
      case "exercise": return exerciseBlock(b, i);
      default:         return el("div", "block", "");
    }
  }

  /* ---- tool / command reference grid ---- */
  function toolsBlock(b) {
    let h = b.title ? `<p class="toolset-cap mono">${b.title}</p>` : "";
    h += '<dl class="tools">';
    b.items.forEach((it) => {
      h += `<div class="tool"><dt><code>${esc(it.cmd)}</code></dt><dd>${it.what}</dd></div>`;
    });
    h += "</dl>";
    return el("div", "block toolset reveal", h);
  }

  /* ---- multiple-choice checkpoint ---- */
  function quizBlock(b, i) {
    const cp = "cp" + i;
    const wrap = el("div", "block quiz reveal");
    wrap.appendChild(el("div", "quiz-q", `<span class="cp-tag mono">checkpoint</span><p>${b.q}</p>`));
    const list = el("ol", "quiz-opts");
    b.options.forEach((opt, oi) => {
      const li = el("li", "quiz-opt");
      li.innerHTML = `<button class="opt-link" type="button"><span class="opt-key mono">${String.fromCharCode(65 + oi)}</span><span class="opt-text">${opt.text}</span></button>`;
      li.querySelector(".opt-link").addEventListener("click", (e) => {
        answer(oi);
      });
      list.appendChild(li);
    });
    wrap.appendChild(list);
    const fb = el("div", "quiz-fb");
    wrap.appendChild(fb);

    function answer(oi) {
      if (wrap.classList.contains("answered")) return;
      wrap.classList.add("answered");
      const opt = b.options[oi];
      const items = list.querySelectorAll(".quiz-opt");
      items[oi].classList.add(opt.correct ? "correct" : "wrong");
      if (!opt.correct) {
        b.options.forEach((o, idx) => { if (o.correct) items[idx].classList.add("correct"); });
      }
      fb.innerHTML =
        `<span class="tag ${opt.correct ? "ok" : "no"}">${opt.correct ? "✓ Correct" : "✗ Not quite"}</span> ` +
        (opt.why || "");
      store[cp] = oi;
      save();
      updateCount();
    }

    if (cp in store) answer(store[cp]);
    return wrap;
  }

  /* ---- approve / deny simulator ---- */
  function approveBlock(b, i) {
    const cp = "cp" + i;
    const wrap = el("div", "block approve reveal");
    wrap.innerHTML = `
      <div class="term">
        <div class="term-head mono"><span class="dot"></span>agent &middot; requesting permission</div>
        <div class="term-body">
          <p class="term-ctx">${b.context}</p>
          <pre class="term-cmd">${esc(b.command)}</pre>
          <p class="term-ask mono">approve this action?
            <button type="button" class="choice" data-c="approve">&rsaquo; approve</button>
            <button type="button" class="choice" data-c="deny">&rsaquo; deny</button>
          </p>
        </div>
        <div class="term-fb"></div>
      </div>`;
    const term = wrap.querySelector(".term");
    const fb = wrap.querySelector(".term-fb");
    wrap.querySelectorAll(".choice").forEach((a) =>
      a.addEventListener("click", (e) => {
        decide(a.getAttribute("data-c"));
      })
    );

    function decide(choice) {
      if (term.classList.contains("answered")) return;
      term.classList.add("answered");
      const correct = choice === b.answer;
      term.classList.add(correct ? "good" : "bad");
      const picked = wrap.querySelector(`.choice[data-c="${choice}"]`);
      if (picked) picked.classList.add("picked");
      const verdict = correct ? "Good call." : "Reconsider.";
      fb.innerHTML =
        `<p><span class="verdict ${correct ? "ok" : "no"}">${verdict}</span> ${b.explain}</p>` +
        (b.nuance ? `<p class="nuance">${b.nuance}</p>` : "");
      store[cp] = choice;
      save();
      updateCount();
    }

    if (cp in store) decide(store[cp]);
    return wrap;
  }

  /* ---- exercise (free, or premium placeholder) ---- */
  function exerciseBlock(b) {
    const wrap = el("div", "block exercise reveal" + (b.premium ? " locked" : ""));
    if (b.premium) {
      wrap.innerHTML = `
        <span class="ex-tag mono">exercise &middot; premium</span>
        <h3>${b.title}</h3>
        <p class="ex-teaser">${b.teaser}</p>
        <p class="lock-note mono">&#128274; part of the premium exercise bundle &middot;
          <a class="link" href="/pages/community">join The Ascent &rarr;</a></p>`;
    } else {
      wrap.innerHTML = `
        <span class="ex-tag mono">exercise</span>
        <h3>${b.title}</h3>
        <div class="ex-body">${b.html}</div>`;
    }
    return wrap;
  }

  /* ---- previous / next lesson navigation ---- */
  function buildPager() {
    const seq = ORDER.filter((x) => (window.ASCEND_LESSONS || {})[x]);
    const idx = seq.indexOf(id);
    const prevId = idx > 0 ? seq[idx - 1] : null;
    const nextId = idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : null;
    const nav = el("nav", "pager");
    nav.setAttribute("aria-label", "Lesson navigation");
    nav.appendChild(pagerLink("prev", prevId));
    nav.appendChild(pagerLink("next", nextId));
    const footer = document.querySelector(".foot");
    if (footer) document.body.insertBefore(nav, footer);
    else document.body.appendChild(nav);
  }
  function pagerLink(dir, lessonId) {
    const isNext = dir === "next";
    const a = document.createElement("a");
    a.className = "pager-link " + dir;
    if (lessonId) {
      const lesson = window.ASCEND_LESSONS[lessonId];
      a.href = "/pages/lesson?id=" + lessonId;
      a.setAttribute("aria-label", `${isNext ? "Next" : "Previous"} lesson: ${lesson.title}`);
      a.innerHTML =
        `<span class="pager-kicker">` +
          `<span class="pager-arrow" aria-hidden="true">${isNext ? "&rarr;" : "&larr;"}</span>` +
          `<span class="pager-dir mono">${isNext ? "next lesson" : "previous lesson"}</span>` +
        `</span>` +
        `<span class="pager-title">${lesson.title}</span>`;
    } else {
      a.href = "/pages/learn";
      a.setAttribute("aria-label", isNext ? "Course complete: return to Fundamentals" : "Return to all Fundamentals lessons");
      a.innerHTML =
        `<span class="pager-kicker">` +
          `<span class="pager-arrow" aria-hidden="true">${isNext ? "&check;" : "&larr;"}</span>` +
          `<span class="pager-dir mono">${isNext ? "course complete" : "all lessons"}</span>` +
        `</span>` +
        `<span class="pager-title">Fundamentals</span>`;
    }
    return a;
  }

  /* ---- shared motion: scroll progress, nav condense, reveal ---- */
  function initMotion() {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reveals = document.querySelectorAll(".reveal");
    if (reduce || !("IntersectionObserver" in window)) {
      reveals.forEach((n) => n.classList.add("in"));
    } else {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((en) => {
          if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
        }),
        { threshold: 0.14, rootMargin: "0px 0px -6% 0px" }
      );
      reveals.forEach((n) => io.observe(n));
    }

    const progress = document.getElementById("progress");
    const vfill = document.getElementById("vfill");
    const nav = document.getElementById("nav");
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight;
        const pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
        if (progress) progress.style.width = pct + "%";
        if (vfill) vfill.style.height = pct + "%";
        if (nav) nav.classList.toggle("scrolled", h.scrollTop > 40);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }
})();
